import FileValidationCard from './FileValidationCard'

/**
 * FileValidationPanel - Displays validation status for all files
 *
 * Shows file header validation results from the backend parser.
 * Non-blocking warnings inform users of potential issues.
 *
 * @param {Object} fileResults - Object mapping filenames to validation results
 */
function FileValidationPanel({ fileResults }) {
  // Check if validation data available (backward compatibility)
  const hasValidationData = Object.values(fileResults || {}).some(
    (result) => result.header_validation
  )

  if (!hasValidationData) {
    return null // Hide panel for old analyses
  }

  // Calculate overall status
  const overallStatus = getOverallStatus(fileResults)
  // Only show alert banner for moderate or critical issues (not low/info)
  const hasSignificantIssues = Object.values(fileResults).some((r) => {
    const summary = r.header_validation?.issue_summary
    return summary && (summary.critical > 0 || summary.moderate > 0)
  })

  return (
    <div className="space-y-4">
      {/* Summary alert if significant issues exist (moderate or critical) */}
      {hasSignificantIssues && (
        <div className={`rounded-lg border p-4 ${getAlertClasses(overallStatus)}`} role="alert">
          <div className="flex items-start gap-3">
            <span className="text-lg">{getAlertIcon(overallStatus)}</span>
            <div className="flex-1">
              <h3 className="font-semibold mb-1">{getAlertTitle(overallStatus)}</h3>
              <p className="text-sm">{getAlertMessage(overallStatus, fileResults)}</p>
            </div>
          </div>
        </div>
      )}

      {/* File cards */}
      <div className="space-y-3">
        {Object.entries(fileResults).map(([filename, result]) => (
          <FileValidationCard key={filename} filename={filename} result={result} />
        ))}
      </div>
    </div>
  )
}

/**
 * Determine overall status across all files
 */
function getOverallStatus(fileResults) {
  const criticalCount = Object.values(fileResults).reduce(
    (sum, r) => sum + (r.header_validation?.issue_summary?.critical || 0),
    0
  )
  const moderateCount = Object.values(fileResults).reduce(
    (sum, r) => sum + (r.header_validation?.issue_summary?.moderate || 0),
    0
  )

  if (criticalCount > 0) return 'error'
  if (moderateCount > 0) return 'warning'
  return 'success'
}

/**
 * Get alert styling classes based on status
 */
function getAlertClasses(status) {
  switch (status) {
    case 'error':
      return 'bg-red-50 border-red-200 text-red-900'
    case 'warning':
      return 'bg-yellow-50 border-yellow-200 text-yellow-900'
    default:
      return 'bg-green-50 border-green-200 text-green-900'
  }
}

/**
 * Get alert icon based on status
 */
function getAlertIcon(status) {
  switch (status) {
    case 'error':
      return '❌'
    case 'warning':
      return '⚠️'
    default:
      return '✅'
  }
}

/**
 * Get alert title based on status
 */
function getAlertTitle(status) {
  switch (status) {
    case 'error':
      return 'Critical Header Issues Detected'
    case 'warning':
      return 'Header Validation Warnings'
    default:
      return 'All Files Valid'
  }
}

/**
 * Get alert message based on status
 */
function getAlertMessage(status, fileResults) {
  // Only count files with moderate or critical issues (not low/info issues)
  // Low-severity issues (like missing headers on Intersection Summary sections) are normal
  const filesWithSignificantIssues = Object.values(fileResults).filter((r) => {
    const summary = r.header_validation?.issue_summary
    return summary && (summary.critical > 0 || summary.moderate > 0)
  }).length
  const totalFiles = Object.keys(fileResults).length

  switch (status) {
    case 'error':
      return `${filesWithSignificantIssues} of ${totalFiles} files have critical issues that may affect parsing accuracy. Review details below for guidance on fixing.`
    case 'warning':
      return `${filesWithSignificantIssues} of ${totalFiles} files have warnings. The analysis can proceed, but you may want to review the issues.`
    default:
      return 'All files passed header validation.'
  }
}

export default FileValidationPanel
